import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from 'src/app/services/user.service';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import * as moment from 'moment';
import { RecaptchaComponent } from 'ng-recaptcha';
import { environment } from 'src/environments/environment';
import { RouteService } from 'src/app/core/services';

@Component({
  selector: 'app-sign-up2',
  templateUrl: './sign-up2.component.html',
  styleUrls: ['./sign-up2.component.css']
})
export class SignUp2Component implements OnInit {

  minDate = "1800-01-01"
  maxDate = moment(new Date()).format('YYYY-MM-DD');
  // TODO: Create a user model instead?
  user = {
    ageUnder18: false,
    firstName: '',
    middleInitial: '',
    lastName: '',
    primaryRole: '',
    primaryRoleOther: '',
    companyName: '',
    dob: '',
    race: '',
    ethnicity: '',
    disabled: '',
    numberOfChildren: '',
    maritalStatus: '',
    familyStatus: '',
    education: '',
    coordinates: '',
    actualState: '',
    captcha: '',

    // fields for users under 18yo (moved from step 3)
    city: '',
    state: '',
    county: '',
  }

  eulaModel = {
    eula: ''
  }

  // Initialize form, only using race in this
  form = this.fb.group({
    checkArray: this.fb.array([])
  })

  geolocating = false;
  submitting = false;
  submitted  = false;

  @ViewChild(RecaptchaComponent, { static: false })
  recaptcha?: RecaptchaComponent;
  recaptchaEnabled = environment.recaptcha.enabled;

  constructor(private userService: UserService, private router: Router, private fb: FormBuilder, private routeService: RouteService) { }

  ngOnInit(): void {
    if (!this.userService.user.signup) {
      this.router.navigate(['/sign-up']);
      return;
    }

    this.user.ageUnder18 = this.userService.user.ageUnder18;

    const checkArray = this.form.controls['checkArray'];

    if (this.user.ageUnder18) {
      checkArray.clearValidators();
      checkArray.updateValueAndValidity();
    } else {
      checkArray.setValidators([Validators.required]);
      checkArray.updateValueAndValidity();
    }

    console.log("component initialized")
  }

  private async checkGeolocation(): Promise<{ position?: GeolocationPosition, error?: GeolocationPositionError }> {
    this.geolocating = true;

    try {
      this.user.coordinates = '';
      this.user.actualState = '';

      const position = await this.userService.getCurrentPosition({ timeout: 10000 });

      const coords = { latitude: position.coords.latitude, longitude: position.coords.longitude };
      // const coords = { latitude: 41.2259616033894, longitude: -95.92708784428619 }; // Nebraska zoo
      // const coords = { latitude: 40.78318752447714, longitude: -73.97784977757433 }; // New York zoo

      this.user.coordinates = coords as any;

      const result = await this.userService.findAddress(coords).toPromise();

      const address = result?.address_components || {};

      this.user.actualState = address.country === 'US' ? address.state : '';

      return { position };
    }
    catch (error) {
      if (error instanceof GeolocationPositionError) {
        return { error };
      }
      else {
        throw error;
      }
    }
    finally {
      this.geolocating = false;
    }
  }

  // Update user in UserService TODO: Theres a better way of doing this
  async onSubmit(form: FormGroup) {
    this.submitted = true;

    if (this.geolocating) {
      return;
    }

    if (this.submitting) {
      return;
    }

    if (form.invalid) {
      return;
    }

    this.submitting = true;

    try {
      // Make sure the form is valid, only checking race in this case
      if (this.form.valid) {
        this.userService.user.firstName = this.user.firstName;
        this.userService.user.middleInitial = this.user.middleInitial;
        this.userService.user.lastName = this.user.lastName;

        if (this.user.ageUnder18) {
          this.userService.user.city = this.user.city;
          this.userService.user.state = this.user.state;
          this.userService.user.county = this.user.county;

          // Check if user is actually in Nebraska state and can sign up for free
          if (this.userService.user.state === 'NE') {
            const { error } = await this.checkGeolocation();

            this.userService.user.coordinates = this.user.coordinates;
            this.userService.user.actualState = this.user.actualState;

            if (error) {
              this.submitted = false;

              if (error.code === GeolocationPositionError.PERMISSION_DENIED) {
                if (confirm('Allow location access to check if you are in Nebraska and can sign up for free.')) {
                  return;
                }
                else {
                  this.routeService.scrollToTop();
                  this.router.navigate(['/sign-up4']);
                  return;
                }
              }
              else {
                if (confirm('Unable to access your location to check if you are in Nebraska and can sign up for free. Please check location settings in browser and try again.')) {
                  return;
                }
              }
            }

            if (!this.user.actualState) {
              alert("Unable to check your location if you are in Nebraska and can sign up for free.");
              this.routeService.scrollToTop();
              this.router.navigate(['/sign-up4']);
              return;
            }

            if (this.user.actualState !== 'NE') {
              alert("Your current location is outside Nebraska, you will have to pay $30 + processing fees for registration.");
              this.routeService.scrollToTop();
              this.router.navigate(['/sign-up4']);
              return;
            }
          }
          else {
            this.routeService.scrollToTop();
            this.router.navigate(['/sign-up4']);
            return;
          }

          const registered = await this.userService.register(this.userService.user, this.user.captcha, null);

          if (!registered) {
            this.user.captcha = '';
            this.recaptcha?.reset();
            this.submitted = false;
          }
        }
        else {
          this.userService.user.primaryRole = this.user.primaryRole;
          this.userService.user.primaryRoleOther = this.user.primaryRole === 'Other' ? this.user.primaryRoleOther : '';
          this.userService.user.companyName = this.user.primaryRole === 'Housing Provider – Owner' || this.user.primaryRole === 'Housing Provider – Property Management' ? this.user.companyName : '';

          // Datepicker returns ISO string. TODO: Implement Date Adapter so datepicker returns MM/DD/YYYY
          // https://material.angular.io/components/datepicker/overview#datepicker-value
          //this.userService.user.dob = new Date(this.user.dob).toLocaleDateString("en-US");
          this.userService.user.dob = moment(this.user.dob).format('YYYY-MM-DD');

          // Race is being converted to a string
          this.userService.user.race = this.form.value.checkArray.toString();
          this.userService.user.ethnicity = this.user.ethnicity;
          this.userService.user.disabled = this.user.disabled;
          this.userService.user.numberOfChildren = this.user.numberOfChildren;
          this.userService.user.maritalStatus = this.user.maritalStatus;
          this.userService.user.familyStatus = this.user.familyStatus;
          this.userService.user.education = this.user.education;

          this.routeService.scrollToTop();
          this.router.navigate(['/sign-up3']);
        }
      }
    }
    catch (err) {
      this.user.captcha = '';
      this.recaptcha?.reset();
      this.submitted = false;
      throw err;
    }
    finally {
      this.submitting = false;
    }
  }

  // Role values
  primaryRoles = [
    { name: 'Renter', value: 'Renter' },
    { name: 'Housing Provider – Owner', value: 'Housing Provider – Owner' },
    { name: 'Housing Provider – Property Management', value: 'Housing Provider – Property Management' },
    { name: 'Other', value: 'Other' },
  ];

  // Race values
  races = [
    { name: 'American Indian or Alaska Native', value: 'American Indian or Alaska Native' },
    { name: 'Asian', value: 'Asian' },
    { name: 'Black or African American', value: 'Black or African American' },
    { name: 'Native Hawaiian or Other Pacific Islander', value: 'Native Hawaiian or Other Pacific Islander' },
    { name: 'White', value: 'White' },
    { name: 'Prefer Not to Say', value: 'Prefer Not to Say' }
  ];

  // Listen to when the checkbox changes
  onCheckboxChange(e: any) {
    const checkArray: FormArray = this.form.get('checkArray') as FormArray;

    // Push in a race if it has been checked
    if (e.target.checked) {
      checkArray.push(new FormControl(e.target.value));
    }

    // If it isn't checked, remove it from the checkArray
    else {
      let i: number = 0;
      checkArray.controls.forEach((item: any) => {
        if (item.value == e.target.value) {
          checkArray.removeAt(i);
          return;
        }
        i++;
      });
    }
  }
}
